import * as echarts from 'echarts'
import React from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate } from 'wdc-cube-react'

import { styled } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'

import DownloadIcon from '@mui/icons-material/Download'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from '@mui/material/Tooltip'
import { DataGrid, GridColDef } from '@mui/x-data-grid'

import {
    buttonSx,
    ChartArea,
    classToFComponent,
    dataGridSx,
    EmptyIcon,
    FCClassContext,
    LifesHubColors
} from 'src/utils/views'
import { formatCompetencia, formatNumber } from 'src/utils/formatter-utils'

import { EChartViewClass } from '../../../the-consumer-market/views/helpers/base-echart'
import { formatNumberToCompactShortForm } from '../../../the-consumer-market/views/helpers/tcm_view_utils'
import { BillingOriginScope } from '../bl_scopes'
import type { BillingOriginLine } from '../bl_types'
import BillingCard, { BillingCardHeader } from './bl_card'

const LOG = Logger.get('BL-ORIGINS')

const INK_SECONDARY = LifesHubColors.muted
const AXIS_LINE = LifesHubColors.border
const GRID_LINE = LifesHubColors.gridLine
const BAR_COLOR = LifesHubColors.chartEmphasis

const useStyles = makeStyles()({
    cardBody: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        flexGrow: 1,
        padding: '0 18px 18px'
    },
    grid: {
        ...dataGridSx
    },
    chart: {
        display: 'flex',
        alignSelf: 'stretch',
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 0,
        minHeight: 200
    }
})

const ViewToggle = styled('div')({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    padding: 3,
    borderRadius: 999,
    backgroundColor: 'var(--grid-line)'
})

const ViewToggleButton = styled('button', {
    shouldForwardProp: (prop) => prop !== 'selected'
})<{ selected?: boolean }>(({ selected }) => ({
    height: 28,
    border: 0,
    borderRadius: 999,
    padding: '0 12px',
    fontSize: 12.5,
    fontWeight: 650,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    color: selected ? '#fff' : 'var(--muted)',
    backgroundColor: selected ? 'var(--ink)' : 'transparent',
    '&:hover': { color: selected ? '#fff' : 'var(--ink)' }
}))

const EmptyState = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '48px 20px',
    textAlign: 'center',
    flexGrow: 1,
    '& h3': { margin: 0, fontSize: 16, color: 'var(--ink)' },
    '& p': { margin: 0, color: 'var(--muted)', maxWidth: 420 }
})

type BillingOriginsViewProps = {
    className?: string
    scope: BillingOriginScope
}

class BillingOriginsTableViewClass implements FCClassContext<BillingOriginsViewProps> {
    scope!: BillingOriginScope
    classes!: ReturnType<typeof useStyles>['classes']

    readonly emitGroupOnly = () => this.scope.onByUserChanged(false).catch(LOG.caught)
    readonly emitByUser = () => this.scope.onByUserChanged(true).catch(LOG.caught)
    readonly emitWithoutVersion = () => this.scope.onByVersionChanged(false).catch(LOG.caught)
    readonly emitByVersion = () => this.scope.onByVersionChanged(true).catch(LOG.caught)
    readonly emitDownload = () => this.scope.onDownload().catch(LOG.caught)

    onSyncState({ scope }: BillingOriginsViewProps) {
        this.scope = scope
        this.classes = useStyles().classes
    }

    render({ className, scope }: BillingOriginsViewProps) {
        this.scope = bindUpdate(React, scope)
        const { classes } = this

        const count = `${scope.context} — ${pluralize(scope.rows.length, 'linha', 'linhas')}`

        return (
            <BillingCard elevation={0} className={className}>
                <BillingCardHeader title="Exportações por origem" count={count} actions={this.renderActions()} />
                {scope.rows.length === 0 && !scope.loading ? (
                    <EmptyState>
                        <EmptyIcon>
                            <InfoOutlinedIcon />
                        </EmptyIcon>
                        <h3>Nenhuma exportação encontrada</h3>
                        <p>Não há exportações no recorte selecionado.</p>
                    </EmptyState>
                ) : (
                    <div className={classes.cardBody}>
                        <DataGrid
                            className={classes.grid}
                            rows={scope.rows}
                            columns={buildColumns(scope.byUser, scope.byVersion)}
                            getRowId={rowIdByOriginLine}
                            loading={scope.loading}
                            disableRowSelectionOnClick
                            density="compact"
                            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                            pageSizeOptions={[10, 25, 50, 100]}
                        />
                    </div>
                )}
            </BillingCard>
        )
    }

    renderActions() {
        const { scope } = this

        return (
            <>
                {scope.loading && <CircularProgress size={16} />}
                <ViewToggle role="group" aria-label="Exibir exportações">
                    <ViewToggleButton
                        type="button"
                        selected={!scope.byUser}
                        aria-pressed={!scope.byUser}
                        onClick={this.emitGroupOnly}
                    >
                        Somente grupo
                    </ViewToggleButton>
                    <ViewToggleButton
                        type="button"
                        selected={scope.byUser}
                        aria-pressed={scope.byUser}
                        onClick={this.emitByUser}
                    >
                        Por usuário
                    </ViewToggleButton>
                </ViewToggle>
                <Tooltip title="Versão = índice de consulta e data da carga de onde os dados saíram">
                    <ViewToggle role="group" aria-label="Agrupar por versão dos dados">
                        <ViewToggleButton
                            type="button"
                            selected={!scope.byVersion}
                            aria-pressed={!scope.byVersion}
                            onClick={this.emitWithoutVersion}
                        >
                            Sem versão
                        </ViewToggleButton>
                        <ViewToggleButton
                            type="button"
                            selected={scope.byVersion}
                            aria-pressed={scope.byVersion}
                            onClick={this.emitByVersion}
                        >
                            Por versão
                        </ViewToggleButton>
                    </ViewToggle>
                </Tooltip>
                <Tooltip title="Baixar a planilha das exportações em tela">
                    <span>
                        <Button
                            sx={buttonSx('default', true)}
                            onClick={this.emitDownload}
                            disabled={scope.downloading || scope.rows.length === 0}
                            startIcon={scope.downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
                        >
                            Baixar planilha
                        </Button>
                    </span>
                </Tooltip>
            </>
        )
    }
}

export const BillingOriginsTableView = classToFComponent(BillingOriginsTableViewClass, React)

class BillingOriginsChartViewClass extends EChartViewClass<BillingOriginsViewProps> {
    scope!: BillingOriginScope
    classes!: ReturnType<typeof useStyles>['classes']

    onSyncState(props: BillingOriginsViewProps) {
        this.scope = props.scope
        this.classes = useStyles().classes
        this.chartData = buildChartData(totalsByOrigin(this.scope.rows))
        this.loaded = true
        super.onSyncState(props)
    }

    override render({ className, scope }: BillingOriginsViewProps): React.JSX.Element {
        const total = scope.rows.reduce((acc, row) => acc + row.qtdExportada, 0)

        return (
            <BillingCard elevation={0} className={className}>
                <BillingCardHeader
                    title="Registros exportados por origem"
                    count={`${pluralize(total, 'registro', 'registros')}`}
                />
                <ChartArea>{super.render({ className: this.classes.chart, scope })}</ChartArea>
            </BillingCard>
        )
    }
}

export const BillingOriginsChartView = classToFComponent(BillingOriginsChartViewClass, React)

type OriginTotal = {
    label: string
    qtdExportada: number
    qtdExportacoes: number
}

function totalsByOrigin(rows: BillingOriginLine[]): OriginTotal[] {
    const byLabel = new Map<string, OriginTotal>()

    for (const row of rows) {
        const total = byLabel.get(row.origemLabel) ?? { label: row.origemLabel, qtdExportada: 0, qtdExportacoes: 0 }
        total.qtdExportada += row.qtdExportada
        total.qtdExportacoes += row.qtdExportacoes
        byLabel.set(row.origemLabel, total)
    }

    return [...byLabel.values()].sort((a, b) => a.qtdExportada - b.qtdExportada)
}

function buildChartData(totals: OriginTotal[]): echarts.EChartsOption {
    const totalGeral = totals.reduce((acc, item) => acc + item.qtdExportada, 0)

    return {
        grid: { left: 8, right: 56, top: 8, bottom: 4, containLabel: true },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params: unknown) => {
                const [item] = (Array.isArray(params) ? params : [params]) as { dataIndex: number }[]
                const total = item ? totals[item.dataIndex] : undefined
                if (!total) {
                    return ''
                }
                const pct = totalGeral > 0 ? (total.qtdExportada / totalGeral) * 100 : 0
                return (
                    `<div style="margin-bottom:6px;font-weight:700;color:${LifesHubColors.ink}">${total.label}</div>` +
                    tooltipRow('Registros', formatNumber(total.qtdExportada)) +
                    tooltipRow('Exportações', formatNumber(total.qtdExportacoes)) +
                    tooltipRow('Participação', `${pct.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`)
                )
            }
        },
        xAxis: {
            type: 'value',
            splitLine: { lineStyle: { color: GRID_LINE } },
            axisLabel: { color: INK_SECONDARY, formatter: (value: number) => formatNumberToCompactShortForm(value) }
        },
        yAxis: {
            type: 'category',
            data: totals.map((item) => item.label),
            axisTick: { show: false },
            axisLine: { lineStyle: { color: AXIS_LINE } },
            axisLabel: { color: INK_SECONDARY, width: 150, overflow: 'truncate' }
        },
        series: [
            {
                name: 'Registros',
                type: 'bar',
                data: totals.map((item) => item.qtdExportada),
                barMaxWidth: 22,
                itemStyle: { color: BAR_COLOR, borderRadius: [0, 4, 4, 0] },
                label: {
                    show: true,
                    position: 'right',
                    color: INK_SECONDARY,
                    formatter: ({ value }) => formatNumberToCompactShortForm(Number(value))
                }
            }
        ]
    }
}

function tooltipRow(name: string, value: string): string {
    return (
        '<div style="display:flex;align-items:center;justify-content:space-between;gap:24px">' +
        `<span>${name}</span>` +
        `<span style="font-weight:600">${value}</span>` +
        '</div>'
    )
}

function pluralize(count: number, singular: string, plural: string): string {
    return `${formatNumber(count)} ${count === 1 ? singular : plural}`
}

function formatDateTime(value: string | null): string {
    if (!value) {
        return '—'
    }

    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('pt-BR')
}

const COMPETENCIA_COLUMN: GridColDef = {
    field: 'mesAno',
    headerName: 'Comp.',
    width: 82,
    valueFormatter: (value) => formatCompetencia(value as string)
}

const ENTITY_COLUMN: GridColDef = {
    field: 'entityName',
    headerName: 'Grupo / Usuário',
    flex: 1,
    minWidth: 170,
    renderCell: ({ row }) => {
        const line = row as BillingOriginLine
        return (
            <span
                style={{ fontWeight: 750 }}
                title={line.entityType === 'USER' ? 'Usuário com franquia própria' : 'Grupo'}
            >
                {line.entityName}
                {line.entityType === 'USER' ? ' (franquia própria)' : ''}
            </span>
        )
    }
}

const USER_COLUMN: GridColDef = {
    field: 'userName',
    headerName: 'Usuário',
    flex: 1,
    minWidth: 150
}

const TAIL_COLUMNS: GridColDef[] = [
    {
        field: 'origemLabel',
        headerName: 'Origem',
        flex: 1,
        minWidth: 180,
        renderCell: ({ row }) => {
            const line = row as BillingOriginLine
            return <span title={line.origem}>{line.origemLabel}</span>
        }
    },
    {
        field: 'qtdExportada',
        headerName: 'Exportado',
        width: 104,
        type: 'number',
        valueFormatter: (value) => formatNumber(Number(value))
    },
    {
        field: 'qtdExportacoes',
        headerName: 'Exportações',
        width: 104,
        type: 'number',
        valueFormatter: (value) => formatNumber(Number(value))
    },
    {
        field: 'ultimaExportacao',
        headerName: 'Última exportação',
        width: 150,
        valueFormatter: (value) => formatDateTime(value as string | null)
    }
]

const VERSION_COLUMN: GridColDef = {
    field: 'versaoDados',
    headerName: 'Versão dos dados',
    flex: 1,
    minWidth: 190,
    renderCell: ({ value }) =>
        value ? (
            <span title={value as string}>{value as string}</span>
        ) : (
            <span style={{ color: 'var(--muted)' }} title="Exportação sem versão gravada (anterior ao registro ou CRM)">
                Não registrada
            </span>
        )
}

function buildColumns(byUser: boolean, byVersion: boolean): GridColDef[] {
    const [origem, ...numbers] = TAIL_COLUMNS
    return [
        COMPETENCIA_COLUMN,
        ENTITY_COLUMN,
        ...(byUser ? [USER_COLUMN] : []),
        origem,
        ...(byVersion ? [VERSION_COLUMN] : []),
        ...numbers
    ]
}

const rowIdByOriginLine = (row: unknown) => {
    const line = row as BillingOriginLine
    return `${line.mesAno}-${line.entityType}-${line.entityId}-${line.userId ?? ''}-${line.origem}-${line.versaoDados ?? ''}`
}
